import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Globe, TrendingUp, Megaphone, CheckCircle, Clock, ArrowRight, ExternalLink, Tag, Zap, BarChart3, Phone, Search, Bot, Link2, Percent, PhoneCall, BrainCircuit } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const UPGRADES = [
  {
    id: 'fb_ig_ads',
    title: 'Facebook & Instagram Ads Management',
    description: 'We create and manage targeted ad campaigns on Facebook and Instagram to drive leads, calls, and foot traffic to your business.',
    icon: Megaphone,
    color: '#2563EB',
    features: [
      'Custom ad creative designed for your brand',
      'Audience targeting based on your ideal customer',
      'A/B testing to maximize ROI',
      'Monthly performance reports in your dashboard',
      'Budget optimization and bid management',
    ],
    regularPrice: '$299/mo',
    dashPrice: '$239/mo',
    savings: 'Save $60/mo',
    note: 'Ad spend billed separately by Meta.',
  },
  {
    id: 'custom_website',
    title: 'Custom Website by Local Branding Pro',
    description: 'Professional website with domain, hosting, SEO, Google Business Profile setup, AI chatbot, and monthly reports — built by LocalBrandingPro.com.',
    icon: Globe,
    color: '#059669',
    features: [
      'Custom domain + fast cloud hosting',
      'On-page SEO, schema markup, and sitemap',
      'Google Business Profile setup & optimization',
      'Google Analytics + Search Console configured',
      'AI chatbot and lead capture forms',
      '24/7 support + monthly performance reports',
    ],
    regularPrice: '$149/mo ($0 down)',
    dashPrice: '$119/mo ($0 down)',
    savings: 'Save $360/year',
    note: 'Or pay $2,000 upfront + $63/mo (regular $79/mo). Both plans include the full package.',
    link: 'https://localbrandingpro.com',
  },
  {
    id: 'seo_boost',
    title: 'Advanced Local SEO Boost',
    description: 'Accelerate your local rankings with advanced citation building, competitor analysis, link building, and monthly SEO strategy sessions.',
    icon: TrendingUp,
    color: '#b45309',
    features: [
      'Deep competitor keyword analysis',
      'Advanced citation building (200+ directories)',
      'Local link building outreach',
      'Monthly strategy call with SEO specialist',
      'Quarterly ranking report with action items',
    ],
    regularPrice: '$199/mo',
    dashPrice: '$159/mo',
    savings: 'Save $480/year',
    note: 'Best for businesses in competitive markets.',
  },
  {
    id: 'call_tracking',
    title: 'Call Tracking',
    description: 'Track every inbound call with dedicated local numbers, call recording, and attribution — know exactly which marketing channel drives each call.',
    icon: PhoneCall,
    color: '#7c3aed',
    features: [
      '5 local tracking numbers',
      '250 local minutes included',
      'Call recording and routing',
      'Source attribution (which ad/page drove the call)',
      'Call & text tracking in your dashboard',
    ],
    regularPrice: '$99/mo',
    dashPrice: '$79/mo',
    savings: 'Save $240/year',
    note: 'Overage: $8/extra number + $0.08/min over 250.',
  },
  {
    id: 'call_tracking_ai',
    title: 'Call Tracking + AI Insights',
    description: 'Everything in Call Tracking plus AI-powered call transcripts, keyword analysis, and conversation intelligence to help you improve sales and service.',
    icon: BrainCircuit,
    color: '#be185d',
    features: [
      'Everything in Call Tracking',
      'AI call transcripts for every call',
      'Keyword and topic analysis',
      'Conversation intelligence reports',
      'Identify top-performing scripts and objections',
    ],
    regularPrice: '$189/mo',
    dashPrice: '$149/mo',
    savings: 'Save $480/year',
    note: 'Best for businesses that want to improve call conversion rates.',
  },
];

const WTS_LBP_BENEFITS = [
  { icon: BarChart3, title: 'Unified Analytics', desc: 'Website traffic, calls, and leads all flow into one dashboard — no switching between tools.' },
  { icon: Search, title: 'SEO That Syncs', desc: 'Your website SEO and local SEO work together. Citations, schema, and GBP all point to one optimized site.' },
  { icon: Phone, title: 'Built-In Call Tracking', desc: 'Every call from your website is tracked and attributed — know exactly which page drives leads.' },
  { icon: Bot, title: 'AI Chatbot Included', desc: 'Capture leads 24/7 with an AI chatbot that feeds directly into your dashboard contacts.' },
  { icon: Link2, title: 'One Team, Zero Gaps', desc: 'Your website, SEO, ads, and marketing are all managed by the same team — nothing falls through the cracks.' },
  { icon: Zap, title: 'Faster Results', desc: 'Integrated systems perform better. Clients with WTS websites see faster ranking improvements and more leads.' },
];

export default function ClientUpgrades({ client, getHeaders }) {
  const { t } = useTranslation('dashboard');
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(null);

  const hdrs = useCallback(() => ({
    ...getHeaders(),
    'Content-Type': 'application/json',
  }), [getHeaders]);

  useEffect(() => {
    fetch(`${API}/api/client/dashboard/upgrades`, { headers: getHeaders() })
      .then(r => r.ok ? r.json() : { requests: [] })
      .then(d => setRequests(d.requests || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [getHeaders]);

  const requestUpgrade = async (upgradeId) => {
    setSubmitting(upgradeId);
    try {
      const res = await fetch(`${API}/api/client/dashboard/upgrades/request`, {
        method: 'POST',
        headers: hdrs(),
        body: JSON.stringify({ upgrade_id: upgradeId }),
      });
      if (res.ok) {
        const data = await res.json();
        setRequests(prev => [...prev, data.request]);
      }
    } catch (e) {
      console.error(e);
    }
    setSubmitting(null);
  };

  const getStatus = (upgradeId) => {
    const req = requests.find(r => r.upgrade_id === upgradeId);
    return req?.status || null;
  };

  const statusBadge = (status) => {
    if (!status) return null;
    const styles = {
      requested: 'bg-amber-900/30 text-amber-400',
      in_progress: 'bg-blue-900/30 text-blue-400',
      active: 'bg-green-50 text-green-400',
      declined: 'bg-red-50 text-red-400',
    };
    const labels = {
      requested: t('upgrades.status.requested'),
      in_progress: t('upgrades.status.in_progress'),
      active: t('upgrades.status.active'),
      declined: t('upgrades.status.declined'),
    };
    return <Badge className={styles[status] || 'bg-gray-800 text-gray-400'} data-testid={`upgrade-status-${status}`}>{labels[status] || status}</Badge>;
  };

  if (loading) return <div className="text-gray-400 animate-pulse text-center py-12 text-sm">{t('upgrades.loading')}</div>;

  // Check if client has a WTS website upgrade active
  const hasWtsWebsite = getStatus('custom_website') === 'active';

  return (
    <div className="space-y-6" data-testid="client-upgrades">

      {/* 20% Dashboard Discount Banner */}
      <div className="bg-gradient-to-r from-blue-600/10 via-purple-600/10 to-green-600/10 border border-blue-500/20 rounded-xl p-5" data-testid="discount-banner">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
            <Percent className="h-4 w-4 text-green-400" />
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-900">{t('upgrades.discountTitle')}</p>
            <p className="text-[11px] text-gray-400">{t('upgrades.discountSubtitle')}</p>
          </div>
        </div>
      </div>

      {/* WTS + LBP Benefits Banner (shown if client doesn't have WTS website) */}
      {!hasWtsWebsite && (
        <Card className="bg-[#0a1628] border-green-800/30 overflow-hidden" data-testid="wts-benefits-banner">
          <CardContent className="pt-6 pb-6">
            <div className="flex items-center gap-2 mb-1">
              <Globe className="h-4 w-4 text-green-400" />
              <p className="text-xs font-bold uppercase tracking-wider text-green-400">{t('upgrades.betterTogether')}</p>
            </div>
            <h3 className="text-base font-bold text-gray-900 mb-1">
              {t('upgrades.betterTogetherTitle')}
            </h3>
            <p className="text-xs text-gray-400 mb-5 max-w-2xl">
              {t('upgrades.betterTogetherDesc')}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {WTS_LBP_BENEFITS.map(b => (
                <div key={b.title} className="flex gap-3 p-3 rounded-lg bg-white border border-gray-200/50">
                  <div className="w-7 h-7 rounded-md bg-green-50 flex items-center justify-center flex-shrink-0">
                    <b.icon className="h-3.5 w-3.5 text-green-400" />
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold text-gray-900">{b.title}</p>
                    <p className="text-[10px] text-gray-500 leading-relaxed mt-0.5">{b.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-5 flex items-center gap-3">
              <Button
                size="sm"
                onClick={() => {
                  const el = document.querySelector('[data-testid="upgrade-card-custom_website"]');
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }}
                className="bg-green-600 hover:bg-green-700 text-xs h-8"
                data-testid="wts-cta-btn"
              >
                <Globe className="h-3 w-3 mr-1.5" /> {t('upgrades.wtsCta')}
              </Button>
              <a href="https://localbrandingpro.com/new-business" target="_blank" rel="noopener noreferrer" className="text-[10px] text-gray-500 hover:text-gray-300 flex items-center gap-1">
                {t('upgrades.learnMoreAt')} <ExternalLink className="h-2.5 w-2.5" />
              </a>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Section Header */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900">{t('upgrades.title')}</h2>
        <p className="text-xs text-gray-500 mt-1">{t('upgrades.subtitle')}</p>
      </div>

      {/* Upgrade Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        {UPGRADES.map(u => {
          const status = getStatus(u.id);
          const Icon = u.icon;
          return (
            <Card key={u.id} className="bg-white border-gray-200 hover:border-gray-200 transition-colors flex flex-col" data-testid={`upgrade-card-${u.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${u.color}20` }}>
                    <Icon className="h-5 w-5" style={{ color: u.color }} />
                  </div>
                  {statusBadge(status)}
                </div>
                <CardTitle className="text-sm text-gray-900 mt-3">{u.title}</CardTitle>
              </CardHeader>
              <CardContent className="flex-1 flex flex-col">
                <p className="text-xs text-gray-400 leading-relaxed mb-4">{u.description}</p>

                <div className="space-y-1.5 mb-4 flex-1">
                  {u.features.map(f => (
                    <div key={f} className="flex items-start gap-2">
                      <CheckCircle className="h-3 w-3 mt-0.5 flex-shrink-0" style={{ color: u.color }} />
                      <span className="text-[11px] text-gray-400">{f}</span>
                    </div>
                  ))}
                </div>

                <div className="pt-3 border-t border-gray-200 space-y-2">
                  {/* Pricing with strikethrough + discount */}
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] text-gray-600 line-through">{u.regularPrice}</span>
                      <span className="text-sm font-bold text-gray-900">{u.dashPrice}</span>
                    </div>
                    <span className="inline-flex items-center gap-1 text-[10px] font-medium text-green-400 bg-green-900/20 px-2 py-0.5 rounded-full mt-1" data-testid="discount-badge">
                      <Tag className="h-2.5 w-2.5" /> {t('upgrades.savingsBadge', { savings: u.savings })}
                    </span>
                  </div>
                  <p className="text-[10px] text-gray-600">{u.note}</p>

                  {u.link && (
                    <a href={u.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[10px] text-blue-400 hover:text-blue-300">
                      {t('upgrades.visitMain')} <ExternalLink className="h-2.5 w-2.5" />
                    </a>
                  )}

                  {!status ? (
                    <Button
                      onClick={() => requestUpgrade(u.id)}
                      disabled={submitting === u.id}
                      className="w-full text-xs h-9 mt-2"
                      style={{ background: u.color }}
                      data-testid={`request-upgrade-${u.id}`}
                    >
                      {submitting === u.id ? (
                        <Clock className="h-3 w-3 mr-1.5 animate-spin" />
                      ) : (
                        <ArrowRight className="h-3 w-3 mr-1.5" />
                      )}
                      {t('upgrades.requestUpgrade')}
                    </Button>
                  ) : status === 'requested' ? (
                    <div className="text-center py-2">
                      <p className="text-[10px] text-amber-400">{t('upgrades.requestedMsg')}</p>
                    </div>
                  ) : status === 'active' ? (
                    <div className="text-center py-2">
                      <p className="text-[10px] text-green-400">{t('upgrades.activeMsg')}</p>
                    </div>
                  ) : null}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
